/**
 * SearchableSelect: a search box plus a dropdown of the server's matches.
 *
 * For pickers over tables too large to list in full (PROD has ~2,800 organizations and
 * millions of contacts). A fixed "first 100 rows" dropdown silently hides most choices, and
 * on edit it hides the record's current value. This component:
 *   - asks the server for up to `limit` matches for the typed term (debounced);
 *   - always keeps the CURRENT value selectable, labelled via `resolveLabel` when it is not
 *     among the matches, so editing a record never shows a blank or a wrong selection;
 *   - says when a search failed instead of showing an empty list;
 *   - pages: when the server says more matches exist, a "Load more" button fetches the next page,
 *     so no match is unreachable behind a full first page (external audit a025747a F2);
 *   - Enter in the search box never submits the surrounding form (external audit a025747a F1):
 *     it would save the OLD selection while the user is still searching.
 */
import React, { useId, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "../../hooks/useDebounce";
import Select from "./Select";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";
import type { SelectOption } from "./Select";
import type { PickerPage } from "../../services/pickerOptions.service";

export interface SearchableSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Server search; `term` is "" before the user types, `page` counts from 0. Return one page of
   * matches and whether another page exists.
   */
  search: (term: string, page: number) => Promise<PickerPage>;
  /** Label for the current value when it is not among the matches. null = not found. */
  resolveLabel?: (value: string) => Promise<string | null>;
  /** Distinct cache namespace per picker, e.g. "task-form-org". */
  queryKeyBase: string;
  /** The "no selection" row. Omit to have no blank row. */
  emptyLabel?: string;
  /** Minimum characters before searching (0 = search on open too). */
  minChars?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  testId?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  value,
  onChange,
  search,
  resolveLabel,
  queryKeyBase,
  emptyLabel,
  minChars = 0,
  placeholder = "Type to search...",
  required,
  disabled,
  error,
  helperText,
  testId,
}) => {
  const selectId = `searchable-select-${useId()}`;
  const [term, setTerm] = useState("");
  const debounced = useDebounce(term.trim(), 300);
  const canSearch = debounced.length >= minChars;

  const queryClient = useQueryClient();
  const results = useInfiniteQuery({
    queryKey: [queryKeyBase, "search", debounced],
    queryFn: ({ pageParam }) => search(debounced, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last: PickerPage, pages: PickerPage[]) =>
      last.hasMore ? pages.length : undefined,
    enabled: canSearch && !disabled,
    retry: 1,
    staleTime: 30_000,
  });

  const matches = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    return (results.data?.pages ?? [])
      .flatMap((p) => p.options)
      .filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
  }, [results.data]);
  const serverNote = results.data?.pages[results.data.pages.length - 1]?.note;
  const currentInMatches = !value || matches.some((o) => o.value === value);

  const current = useQuery({
    queryKey: [queryKeyBase, "label", value],
    queryFn: () => (resolveLabel ? resolveLabel(value) : Promise.resolve(null)),
    enabled: !!value && !currentInMatches && !!resolveLabel,
    staleTime: 5 * 60_000,
  });

  const options = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = [];
    if (emptyLabel !== undefined) {
      list.push({ value: "", label: emptyLabel });
    }
    if (value && !currentInMatches) {
      list.push({
        value,
        label:
          current.data ??
          (current.isLoading
            ? "(loading current selection…)"
            : "(current selection)"),
      });
    }
    return list.concat(matches);
  }, [
    emptyLabel,
    value,
    currentInMatches,
    current.data,
    current.isLoading,
    matches,
  ]);

  let status: string | undefined;
  if (!canSearch) {
    status = `Type at least ${minChars} characters to search`;
  } else if (results.isError) {
    status = "Search failed, please try again.";
  } else if (results.isFetching && !results.isFetchingNextPage) {
    status = "Searching…";
  } else if (debounced && matches.length === 0) {
    status = "No matches";
  } else if (serverNote) {
    status = serverNote;
  } else if (results.hasNextPage) {
    status = `Showing ${matches.length} matches. Load more, or narrow the search (an ID works too).`;
  }

  return (
    <div data-testid={testId}>
      <label
        htmlFor={selectId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        // Enter must never submit the surrounding form (TaskForm, Edit Contact, user edit): the
        // search is live, so Enter has nothing to do here.
        onKeyDown={preventEnterSubmit}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={`Search ${label}`}
        data-testid={testId ? `${testId}-search` : undefined}
        className="block w-full mb-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <Select
        id={selectId}
        value={value}
        onChange={(e) => {
          const picked = e.target.value;
          // Remember the chosen row's label so it stays named after the search term changes.
          const match = matches.find((o) => o.value === picked);
          if (match) {
            queryClient.setQueryData(
              [queryKeyBase, "label", picked],
              match.label,
            );
          }
          onChange(picked);
        }}
        options={options}
        required={required}
        disabled={disabled}
        error={error}
        helperText={status ?? helperText}
        data-testid={testId ? `${testId}-select` : undefined}
      />
      {results.hasNextPage && !disabled && (
        <button
          type="button"
          onClick={() => results.fetchNextPage()}
          disabled={results.isFetchingNextPage}
          data-testid={testId ? `${testId}-load-more` : undefined}
          className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
        >
          {results.isFetchingNextPage
            ? "Loading…"
            : `Load more matches (${matches.length} shown)`}
        </button>
      )}
    </div>
  );
};

export default SearchableSelect;
